import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

export function generateTask() {
  const possibleDurations = [12, 24, 36];
  const possibleDrops = [50, 100, 200];

  const n = possibleDurations[Math.floor(Math.random() * possibleDurations.length)];
  const drop = possibleDrops[Math.floor(Math.random() * possibleDrops.length)];
  const r = -drop;

  // Wybieramy an tak, żeby a1 = an + (n - 1) * r było dodatnie
  const minAn = Math.ceil((n - 1) * -r + 50); // zabezpieczenie przed a1 < 0
  const maxAn = minAn + 400;
  const an = Math.floor(Math.random() * (maxAn - minAn + 1)) + minAn;

  const a1 = an - (n - 1) * r;
  const Sn = ((a1 + an) / 2) * n;

  return {
    type: "FindLoanInstallmentsTask",
    data: { n, r, Sn, a1, an },
    answer: { a1, an, n }
  };
}





export function renderTask({ data }) {
  const { n, r, Sn } = data;
  return (
    <>
      <p>
        Marta spłaciła kredyt o łącznej wysokości <InlineMath math={`${Sn}`} /> zł 
        w ciągu <InlineMath math={`${n}`} /> miesięcy.
      </p>
      <p>
        Każda kolejna rata była niższa od poprzedniej o <InlineMath math={`${Math.abs(r)}`} /> zł.
      </p>
      <p>
        Oblicz wysokość pierwszej i ostatniej raty.
      </p>
    </>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { n } = data;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-4 items-center flex-wrap">
        <div className="flex items-center gap-2">
          <span><InlineMath math={`a_1 =`} /></span>
          <FractionInput
            value={userAnswer?.a1 || ""}
            onChange={(val) => onChange({ ...userAnswer, a1: val })}
            disabled={disabled}
            label="a_1"
            suffixLatex="\text{zł}"
            compactPreview={true}
          />
        </div>
        <div className="flex items-center gap-2">
          <span><InlineMath math={`a_{${n}} =`} /></span>
          <FractionInput
            value={userAnswer?.an || ""}
            onChange={(val) => onChange({ ...userAnswer, an: val })}
            disabled={disabled}
            label={`a_{${n}}`}
            suffixLatex="\text{zł}"
            compactPreview={true}
          />
        </div>
      </div>
    </div>
  );
}



export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;

  const a1Given = userAnswer.a1?.parsed;
  const anGiven = userAnswer.an?.parsed;

  if (a1Given === null || anGiven === null) return false;

  return (
    Math.abs(a1Given - data.a1) < 0.01 &&
    Math.abs(anGiven - data.an) < 0.01
  );
}

export function renderAnswer({ data }) {
  const { a1, an, n } = data;

  return (
    <InlineMath
      math={`a_1 = ${a1}\\,\\text{zł}, \\quad a_{${n}} = ${an}\\,\\text{zł}`}
    />
  );
}

export function renderExplanation({ data }) {
  const { n, r, Sn, a1, an } = data;
  const rPart = r * (n - 1);
  const rSign = rPart >= 0 ? "+" : "-";
  const absRPart = Math.abs(rPart);

  const numerator = 2 * Sn;
  const denominator = n;
  const rhs = 2 * a1 + rPart;

  return (
    <div className="space-y-4">
      <p><b>Krok 1:</b> Wzór na sumę:</p>
      <BlockMath math={`S_n = \\frac{n}{2}(a_1 + a_n)`} />

      <p><b>Krok 2:</b> Podstawiamy dane:</p>
      <BlockMath math={`${Sn} = \\frac{${n}}{2}(a_1 + a_{${n}})`} />

      <p><b>Krok 3:</b> Wiemy też, że:</p>
      <BlockMath math={`a_{${n}} = a_1 + (${n} - 1)(${r})`} />
      <BlockMath math={`a_{${n}} = a_1 ${rSign} ${absRPart}`} />

      <p><b>Krok 4:</b> Rozwiązujemy układ równań:</p>
      <BlockMath math={`\\left\\{\\begin{array}{l}
        ${Sn} = \\frac{${n}}{2}(a_1 + a_{${n}}) \\\\
        a_{${n}} = a_1 ${rSign} ${absRPart}
      \\end{array}\\right.`} />

      <p>Podstawiamy <InlineMath math={`a_{${n}}`} /> do wzoru na sumę:</p>
      <BlockMath math={`${Sn} = \\frac{${n}}{2}(a_1 + a_1 ${rSign} ${absRPart})`} />
      <BlockMath math={`${Sn} = \\frac{${n}}{2}(2a_1 ${rSign} ${absRPart})`} />

      <p>Przemnażamy obie strony równania przez 2:</p>
      <BlockMath math={`2 \\cdot ${Sn} = ${n}(2a_1 ${rSign} ${absRPart})`} />
      <BlockMath math={`${numerator} = ${n}(2a_1 ${rSign} ${absRPart})`} />

      <p>Dzielimy przez {n}:</p>
      <BlockMath math={`\\frac{${numerator}}{${n}} = 2a_1 ${rSign} ${absRPart}`} />

      <p>Obliczamy wartość po lewej stronie:</p>
      <BlockMath math={`${Math.round(numerator / n)} = 2a_1 ${rSign} ${absRPart}`} />

      <p>Przenosimy {absRPart} na drugą stronę:</p>
      <BlockMath math={`2a_1 = ${Math.round(numerator / n)} ${rPart >= 0 ? "-" : "+"} ${absRPart}`} />
      <BlockMath math={`2a_1 = ${2 * a1}`} />

      <p>Dzielimy przez 2:</p>
      <BlockMath math={`a_1 = ${a1}`} />

      <p>Obliczamy <InlineMath math={`a_{${n}}`} />:</p>
      <BlockMath math={`a_{${n}} = a_1 ${rSign} ${absRPart} = ${a1} ${rSign} ${absRPart} = ${an}`} />
    </div>
  );
}


export function renderUserAnswer(userAnswer) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;

  const extractValue = (val) =>
    typeof val === "object" && val !== null ? val.raw : val;

  const a1 = extractValue(userAnswer?.a1);
  const an = extractValue(userAnswer?.an);
  const n = extractValue(userAnswer?.n) || "n";

  if (!a1 && !an) return <i>(brak odpowiedzi)</i>;

  return (
    <InlineMath
      math={`a_1 = ${a1}\\,\\text{zł}, \\quad a_{${n}} = ${an}\\,\\text{zł}`}
    />
  );
}


